import type { Translations } from "./types";

export const en: Translations = {
  nav: {
    home: "Home",
    features: "Features",
    roadmap: "Roadmap",
    faq: "FAQ",
    blog: "Blog",
    pricing: "Pricing",
    contact: "Contact",
    joinWaitlist: "Start Free Trial",
    signIn: "Sign In",
  },
  footer: {
    tagline: "A product by EtheraEdge. Built for tutors who want to focus on teaching, not admin.",
    pages: "Pages",
    company: "Company",
    contact: "Contact",
    social: "Social",
    legal: "Legal",
    copyright: "EtheraEdge Limited. All rights reserved.",
    builtFor: "Built for the next generation of tutoring businesses",
    home: "Home",
    blog: "Blog",
    terms: "Terms",
    privacy: "Privacy",
    aboutEtheraEdge: "About EtheraEdge",
  },
  homepage: {
    hero: {
      tagline: "Now Live",
      headline: "Stop chasing fees. Start teaching.",
      subheadline:
        "Try Scale free for 30 days — no credit card needed. Invoices generate automatically, WhatsApp reminders send themselves, and payments track without lifting a finger.",
      cta: "Start 30-Day Free Trial",
      secondaryCta: "Already know you need more? See pricing →",
      trustBadges: [
        "30-day free trial of Scale",
        "WhatsApp reminders with payment links",
        "Auto-track payments",
        "Fee analytics across branches",
      ],
    },
    problem: {
      title: "Sound familiar?",
      subtitle: "Here's what tutors tell us every day:",
      painPoints: [
        {
          title: "Endless follow-ups for payments",
          description:
            "Phone calls, WhatsApp messages, awkward reminders. You're a teacher, not a debt collector.",
        },
        {
          title: "Same parent updates, over and over",
          description:
            "Each week, same conversation in WhatsApp, same messages in Excel. You're not a secretary. But you feel like one.",
        },
        {
          title: "Hours every week wasted on admin",
          description:
            "That time could be lesson prep, creating better content, or just... teaching. Instead, you're chasing money.",
        },
      ],
    },
    solution: {
      title: "With EtheraGuru, all of that disappears.",
      features: [
        {
          title: "Smart Invoicing",
          description:
            "Auto-generate invoices on your chosen day. Void mistakes, apply discounts, and carry forward balances and credits automatically.",
        },
        {
          title: "WhatsApp Reminders That Work",
          description:
            "Automated reminders on invoice generation, before due date, and after overdue — all configurable, and they respect each parent's reminder consent.",
        },
        {
          title: "Payment Links That Self-Renew",
          description:
            "Send payment links with reminders. Payments auto-track to invoices. Expired links renew themselves so parents never hit a dead end.",
        },
        {
          title: "Consolidated Billing",
          description:
            "One smart invoice per student. Unpaid balances, partial payments, and credits all roll forward automatically. No spreadsheet gymnastics.",
        },
        {
          title: "Fee Analytics Across Branches",
          description:
            "Track collection rates, revenue trends, and overdue patterns across the dashboard, courses, batches, and every branch you operate.",
        },
        {
          title: "Flexible Enrolment & Catch-up Billing",
          description:
            "Students can join mid-cycle. Pro-rata or full-month catch-up invoices generate automatically — including from bulk CSV imports.",
        },
      ],
    },
    roadmap: {
      title: "Built for the Future",
      subtitle: "Phases 1 and 2 are live now. More features coming regularly.",
      phases: [
        {
          phase: "Phase 1",
          title: "Fee Collection",
          description: "Automated reminders, payment tracking",
          status: "Live Now",
          icon: "money",
          glyph: "invoice",
        },
        {
          phase: "Phase 2",
          title: "Fee Analytics & Multi-Branch",
          description:
            "Collection rates, revenue trends, and overdue patterns across courses, batches, and every branch you operate.",
          status: "Live Now",
          icon: "file",
          glyph: "analytics",
        },
        {
          phase: "Coming Soon",
          title: "File Sharing Module",
          description:
            "Share notes, assignments, and recordings with students and parents over WhatsApp",
          status: "In Development",
          icon: "file",
          glyph: "files",
        },
        {
          phase: "Coming Soon",
          title: "Scheduling & Attendance",
          description:
            "Class schedules, attendance tracking, and automated reports across verticals",
          status: "Coming Soon",
          icon: "users",
          glyph: "calendar",
        },
        {
          phase: "Coming Soon",
          title: "Parent Updates",
          description: "Progress reports, performance insights, one-click sharing",
          status: "Planned",
          icon: "bell",
          glyph: "parent",
        },
      ],
    },
    valueGrowth: {
      title: "Built With You, For You",
      subtitle:
        "Your feedback shapes our product. Every month, we add features that solve real problems.",
      cards: [
        {
          title: "Try Before You Pay",
          description:
            "Every signup starts with a 30-day trial of full Scale features — no credit card needed. See a full billing cycle before you decide.",
          icon: "heart",
        },
        {
          title: "Upgrade When You're Ready",
          description:
            "Clear limits, clear upgrade path. No surprise bills, no gaming. Start on Lite and move to Scale when your business demands it.",
          icon: "growth",
        },
        {
          title: "Modular Pricing, Not Feature Gating",
          description:
            "Core features — invoicing, reminders, payments, analytics — are on every plan. Multi-branch is a paid add-on on Scale (File Sharing is coming soon), so dance studios don't subsidise modules they'll never use.",
          icon: "gift",
        },
      ],
      bottomNote:
        "We're building the next-gen tutoring management system — guided by your feedback.",
    },
    pricing: {
      title: "Simple pricing. Clear limits. Upgrade when you grow.",
      subtitle:
        "Every plan includes unlimited students, unlimited tutors, and all features. Pick your message volume.",
      launchBadge: "🚀 30-Day Free Trial — No Card Required",
      annualBilling: {
        monthlyLabel: "Monthly",
        annualLabel: "Annual",
        saveBadge: "Save 16%",
      },
      labels: {
        messages: "Messages",
        storage: "Storage",
        branches: "Branches",
        included: "What's included",
      },
      tiers: [
        {
          id: "lite",
          name: "Lite",
          price: "₹1,499",
          period: "/month",
          annualPrice: "₹14,990/year",
          annualSavings: "Save ₹2,998 (16%)",
          description:
            "The stepping-stone plan. For smaller operators or businesses with predictable, on-time payers.",
          valueFraming:
            "50–100 students, conservative reminder volumes. Add-ons not available — upgrade to Scale when you need them.",
          whatsappLimit: "700",
          storageLimit: "5 GB",
          branchesIncluded: "1",
          canBuyExtras: false,
          supportLevel: "WhatsApp (48hr response)",
          features: [
            "Unlimited students",
            "Unlimited tutors",
            "700 WhatsApp messages/month",
            "5 GB file storage",
            "1 branch",
            "All core features included",
            "WhatsApp support (48hr response)",
          ],
          cta: "Start Free Trial",
          highlighted: false,
          badge: "Best Value",
          belowCta: "30-day free trial of Scale — no card required",
        },
        {
          id: "scale",
          name: "Scale",
          price: "₹2,499",
          period: "/month",
          annualPrice: "₹24,990/year",
          annualSavings: "Save ₹4,998 (16%)",
          description: "For coaching centres and academies running real operations.",
          valueFraming:
            "100–500 students. Multi-branch add-on available, File Sharing coming soon. The only tier with extras.",
          whatsappLimit: "1,500",
          storageLimit: "25 GB",
          branchesIncluded: "1",
          canBuyExtras: true,
          extraMessagePrice: "₹1.25/msg",
          supportLevel: "Priority (24hr response)",
          features: [
            "Unlimited students",
            "Unlimited tutors",
            "1,500 WhatsApp messages/month",
            "25 GB file storage",
            "1 branch included",
            "All core features included",
            "✅ Buy extra messages at ₹1.25/msg",
            "✅ Multi-branch add-on at ₹499/branch/month",
            "⏳ File Sharing module (coming soon)",
            "Priority support (24hr response)",
          ],
          cta: "Start Free Trial",
          highlighted: true,
          badge: "Most Popular",
          belowCta: "30-day free trial — no card required",
        },
        {
          id: "custom",
          name: "Custom",
          price: "Talk to us",
          period: "",
          description: "For institutions and multi-branch operators with custom needs.",
          valueFraming:
            "4+ branches, sustained 1,500+ messages/month, or custom SLAs and integrations.",
          whatsappLimit: "Custom",
          storageLimit: "Custom",
          branchesIncluded: "Unlimited",
          canBuyExtras: true,
          extraMessagePrice: "Custom rates",
          supportLevel: "Dedicated account manager",
          features: [
            "Unlimited students",
            "Unlimited tutors",
            "Custom WhatsApp quota",
            "Custom storage",
            "Unlimited branches (no per-branch fees)",
            "All modules included",
            "All core features included",
            "Dedicated account manager",
            "Custom integrations & SLAs",
            "Invoice-based billing available",
          ],
          cta: "Contact Us",
          ctaLink: "mailto:hello@etheraguru.com",
          highlighted: false,
          isCustom: true,
          belowCta: "For 4+ branches or 1,500+ messages/month sustained.",
        },
      ],
      payAsYouGo: {
        title: "Need More Than 1,500 Messages?",
        note: "Scale is the only tier with extras — buy more messages at ₹1.25 each. Lite users upgrade to Scale when they need more.",
        steps: [],
        spendCap: {
          icon: "💡",
          title: "Clear upgrade path",
          description:
            "Lite → Scale → Custom. Hit your limit on Lite? Move up. Need branches or File Sharing? That's the Scale upgrade signal.",
        },
      },
      roiPositioning: {
        title: "Why This Works",
        pillars: [
          {
            title: "Money Recovered",
            description: "Automated reminders mean fewer missed payments and faster collection.",
          },
          {
            title: "Time Saved",
            description: "No more manual follow-ups. Spend that time teaching or resting.",
          },
          {
            title: "Modular, Not Bundled",
            description:
              "Pay for what your vertical actually needs. Multi-branch is a paid add-on (File Sharing coming soon), not bundled into the price.",
          },
        ],
        featureGrowth:
          "Core features included on every plan. Add-ons are Scale-only and bundled in Custom.",
      },
    },
    social: {
      title: "Stay Connected",
      subtitle: "Follow us for updates, tips, and behind-the-scenes content",
    },
    finalCta: {
      headline: "Ready to stop chasing fees?",
      subheadline:
        "Start your 30-day free trial with full Scale features. No credit card. No commitment. Just better fee collection.",
      cta: "Start Free Trial",
      alternative:
        "Not sure which plan? Start with the 30-day trial — we'll help you decide when it ends.",
      privacyNote: "30-day trial of all Scale features. No credit card. Cancel anytime.",
      whatsappAlternative: "Questions? Chat with us on WhatsApp →",
    },
    faq: {
      title: "Frequently Asked Questions",
      items: [
        {
          question: "Do I need a credit card to start?",
          answer: "No. Your 30-day trial requires no payment info. Just sign up and start.",
        },
        {
          question: "What payment methods do parents use?",
          answer:
            "Parents can pay via UPI, credit/debit cards, net banking, or digital wallets. Money goes directly to your bank account.",
        },
        {
          question: "What if EtheraGuru doesn't work for me?",
          answer:
            "Start free and see for yourself. If you're on a paid plan and don't see value, cancel anytime — no questions asked.",
        },
        {
          question: "Does EtheraGuru charge for payment collection?",
          answer:
            "No. Payment links are included in all plans. We help you set up your Razorpay account, and payments go directly to your bank. Razorpay charges their standard fees (~2%) — we don't add any markup.",
        },
        {
          question: "Which plan should I start on?",
          answer:
            "Start with the 30-day trial of Scale — you get all features free for a month. At day 30, choose Lite (₹1,499) if you stay under 700 messages and don't need branches, or stay on Scale (₹2,499) for 1,500 messages and multi-branch (File Sharing coming soon).",
        },
      ],
    },
  },
  pricing: {
    hero: {
      title: "Clear Pricing - No Hidden Fees",
      subtitle: "All WhatsApp costs included. No extra fees. What you see is what you pay.",
    },

    roi: {
      title: "See: How Much Money You're Losing",
      description: "Calculate how much revenue is delayed when fees are paid late.",
      inputs: {
        students: "Number of Students",
        avgFee: "Average Fee Per Student (₹)",
        latePercent: "What % of fees are paid late",
        avgDaysLate: "Average days late",
      },
      results: {
        monthlyRevenue: "Monthly Revenue",
        delayedRevenue: "Revenue Delayed Each Month",
        cashFlowLost: "Cash Flow Lost",
        potentialRecovery: "Potential Recovery with EtheraGuru",
        roi: "ROI",
      },
      cta: "Join Waitlist to Start Recovering",
    },
    comparisonTable: {
      title: "Compare All Plans",
      headers: {
        feature: "Feature",
        lite: "Lite",
        scale: "Scale",
        custom: "Custom",
      },
      rows: [
        {
          feature: "Price",
          lite: "₹1,499/mo",
          scale: "₹2,499/mo",
          custom: "Talk to us",
        },
        {
          feature: "WhatsApp messages",
          lite: "700",
          scale: "1,500",
          custom: "Custom",
        },
        {
          feature: "Extra messages",
          lite: "❌",
          scale: "✅ ₹1.25/msg",
          custom: "Custom",
        },
        {
          feature: "File storage",
          lite: "5 GB",
          scale: "25 GB",
          custom: "Custom",
        },
        {
          feature: "Branches included",
          lite: "1",
          scale: "1",
          custom: "Unlimited",
        },
        {
          feature: "Multi-branch add-on",
          lite: "❌",
          scale: "✅ ₹499/branch/mo",
          custom: "Included",
        },
        {
          feature: "File Sharing add-on",
          lite: "❌",
          scale: "Coming soon",
          custom: "Coming soon",
        },
        {
          feature: "Students",
          lite: "Unlimited",
          scale: "Unlimited",
          custom: "Unlimited",
        },
        {
          feature: "Tutors",
          lite: "Unlimited",
          scale: "Unlimited",
          custom: "Unlimited",
        },
        {
          feature: "Fee reminders",
          lite: "✅",
          scale: "✅",
          custom: "✅",
        },
        {
          feature: "Invoice generation",
          lite: "✅",
          scale: "✅",
          custom: "✅",
        },
        {
          feature: "Payment tracking",
          lite: "✅",
          scale: "✅",
          custom: "✅",
        },
        {
          feature: "Batch management",
          lite: "✅",
          scale: "✅",
          custom: "✅",
        },
        {
          feature: "Fee analytics",
          lite: "✅",
          scale: "✅",
          custom: "✅",
        },
        {
          feature: "Consolidated billing",
          lite: "✅",
          scale: "✅",
          custom: "✅",
        },
        {
          feature: "SLA",
          lite: "❌",
          scale: "❌",
          custom: "✅",
        },
        {
          feature: "Support",
          lite: "WhatsApp (48hr)",
          scale: "Priority (24hr)",
          custom: "Dedicated",
        },
      ],
    },
    dedicatedFaq: {
      title: "Pricing FAQ",
      items: [
        {
          question: "What happens when I hit my message limit?",
          answer:
            "On Lite, messages pause until next month, or you can upgrade to Scale immediately. Scale users can buy extras at ₹1.25/msg instead.",
        },
        {
          question: "Why can't I buy extra messages on Lite?",
          answer:
            "Lite is the simple, predictable plan. If you consistently need more than 700 messages, the pattern of your business has moved past Lite — Scale is where you go. Only Scale includes extras as a premium feature.",
        },
        {
          question: "Why should I pay ₹2,499 for Scale when Lite is ₹1,499?",
          answer:
            "Scale is for coaching centres and academies running real operations. You get 1,500 messages (vs 700), 25 GB storage (vs 5 GB), priority support, the ability to buy extras at ₹1.25/msg, plus access to the Multi-branch add-on (File Sharing coming soon).",
        },
        {
          question: "What add-ons are available on Scale?",
          answer:
            "Multi-branch at ₹499 per additional branch per month. The File Sharing module is coming soon — pricing will be announced when it launches. Add-ons are Scale-only — Lite doesn't have them. Custom includes all add-ons bundled.",
        },
        {
          question: "Can I upgrade or downgrade anytime?",
          answer:
            "Yes. Upgrade instantly — your new limit applies right away. Downgrade at the end of your billing cycle. No lock-in.",
        },
        {
          question: "Do unused messages roll over?",
          answer: "No, limits reset monthly. There's no penalty for not using them all.",
        },
        {
          question: "Why are students and tutors unlimited on all plans?",
          answer:
            "They don't cost us anything, so why should we limit them? We only charge for what costs us money: WhatsApp messages and file storage.",
        },
        {
          question: "What about annual billing?",
          answer:
            "Save 16% (2 months free) with annual billing. Lite: ₹14,990/year, Scale: ₹24,990/year.",
        },
        {
          question: "When should I consider Custom pricing?",
          answer:
            "If you have 4+ branches, need 1,500+ messages/month consistently, run multiple verticals under one parent organisation, or need custom SLAs, integrations, or invoice-based billing. Email hello@etheraguru.com.",
        },
      ],
    },
  },
  blog: {
    title: "Tutoring business blog",
    subtitle: "Insights, updates, and tips for tutoring business owners",
    noPosts: "No posts yet. Check back soon!",
    readMore: "Read more",
    previous: "Previous",
    next: "Next",
    showing: "Showing",
    of: "of",
    posts: "posts",
    showAll: "Show all languages",
    showingOnly: "Showing",
    postsIn: "posts in",
    otherPosts: "other language posts available",
    backToBlog: "Back to Blog",
    backToAllPosts: "Back to all posts",
  },
  pwa: {
    install: "Install App",
    iosTitle: "Install EtheraGuru",
    iosDescription: "To install this app on your iOS device:",
    iosStep1: "Tap the Share button",
    iosStep2: "Scroll down and tap 'Add to Home Screen'",
    iosStep3: "Tap 'Add' to confirm",
    bannerTitle: "Install EtheraGuru",
    bannerDescription: "Add to home screen for quick access",
  },
  contact: {
    title: "Contact Us",
    subtitle:
      "Have questions about EtheraGuru? We're here to help. Reach out via WhatsApp and we'll respond as soon as possible.",
    eyebrow: "Talk to us",
    responseTime: "We usually reply within a couple of hours",
    statusOnline: "Online now",
    statusAway: "Away right now",
    realPersonTitle: "A real person, not a bot",
    realPersonBody: "Your message reaches our team directly — no ticket queue, no runaround.",
    quickRepliesTitle: "Quick to reply",
    privacyTitle: "Private by default",
    privacyBody: "We only use your message to help you. Never shared, never spammed.",
    quickStartTitle: "Not sure where to start? Tap a question:",
    quickPromptPricing: "What will it cost for my centre?",
    quickPromptDemo: "Can I get a quick demo?",
    quickPromptSwitch: "How do I move from spreadsheets?",
    typingLabel: "typing…",
    emailFallbackTitle: "Prefer email?",
    emailFallbackDescription: "You can also reach us at contact@etheraedge.com",
    whatsappCardHeader: "Chat with us on WhatsApp",
    whatsappCardBody:
      "Have questions? We're here to help. Start a WhatsApp conversation and we'll respond as soon as possible.",
    whatsappAgentName: "EtheraGuru Support",
    whatsappAvailability: "Mon - Sat, 12:30 PM - 9:30 PM IST",
    whatsappDefaultMessage: "Hi! I'd like to know more about EtheraGuru.",
    widgetHeader: "Chat with us",
    widgetBody: "Hey there! How can we help you today? Tap below to start a WhatsApp conversation.",
    widgetTooltip: "Chat on WhatsApp",
    startChatCta: "Start Chat",
    startWhatsappChatCta: "Start WhatsApp Chat",
    messageLabel: "Your message",
    messagePlaceholder: "Type your message...",
  },
};
